import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { AutoAwesome as AutoAwesomeIcon } from "@mui/icons-material";
import { FaVial } from "react-icons/fa";
import useAuth from "../../../hooks/useAuth";
import { useAlert } from "../../../context/AlertProvider";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { ChipListSelect, FormTextField } from "../../../components/atoms";
import { DrawerField, tileInputProps } from "../../../components/molecules";
import TemplateListPage, { statusParam } from "../../../components/organisms/TemplateListPage";
import TemplateEditorDrawer from "../../../components/organisms/TemplateEditorDrawer";
import { StatusPill } from "../../../components/organisms/ClinicRecordList";
import { fullDateTime, relativePast } from "../../Secretary/PatientsPage/patientListFormat";
import {
  useCreateDoctorLabTestTemplateMutation,
  useListDoctorLabTestTemplatesMutation,
  useSetDoctorLabTestTemplateStatusMutation,
  useUpdateDoctorLabTestTemplateMutation,
} from "../../../store/doctorApi";
import { useListLabTestsMutation } from "../../../store/systemVariablesApi";

export default function DoctorLaboratoryTemplates() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();

  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", tests: [] });
  const [labTests, setLabTests] = useState([]);
  const bootRef = useRef(false);

  const [listDoctorLabTestTemplates] = useListDoctorLabTestTemplatesMutation();
  const [createDoctorLabTestTemplate, { isLoading: isCreateLoading }] =
    useCreateDoctorLabTestTemplateMutation();
  const [updateDoctorLabTestTemplate, { isLoading: isUpdateLoading }] =
    useUpdateDoctorLabTestTemplateMutation();
  const [setDoctorLabTestTemplateStatus] = useSetDoctorLabTestTemplateStatusMutation();
  const [listLabTests] = useListLabTestsMutation();

  const isSaving = isCreateLoading || isUpdateLoading;

  const fetchList = async () => {
    if (!token) return;
    setErrorMsg("");
    setIsLoading(true);
    try {
      const res = await listDoctorLabTestTemplates({
        token,
        query,
        status: statusParam(status),
        page: page + 1,
        limit: rowsPerPage,
      }).unwrap();
      setItems(res?.data?.templates || []);
      setTotal(res?.data?.pagination?.total || 0);
    } catch (e) {
      setErrorMsg(e?.data?.message || "Şablonlar alınamadı.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLabTests = async () => {
    if (!token) return;
    try {
      const res = await listLabTests({ token, status: "active", limit: 500 }).unwrap();
      setLabTests(res?.data?.labTests || res?.data?.items || []);
    } catch (e) {
      setLabTests([]);
    }
  };

  useEffect(() => {
    if (!token || bootRef.current) return;
    bootRef.current = true;
    fetchList();
    fetchLabTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, status, page, rowsPerPage]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", tests: [] });
    setOpen(true);
  };

  const openEdit = (template) => {
    const tests = (template.items || []).map(
      (it) =>
        labTests.find((t) => String(t._id) === String(it.labTest)) || {
          _id: it.labTest,
          name: it.name,
          unit: it.unit,
          referenceValue: it.referenceValue,
        }
    );
    setEditing(template);
    setForm({ name: template.name || "", tests });
    setOpen(true);
  };

  const autoGenerate = () => {
    if (labTests.length === 0) return;
    const count = Math.floor(Math.random() * 5) + 3;
    const selected = [...labTests]
      .sort(() => Math.random() - 0.5)
      .slice(0, Math.min(count, labTests.length));
    setForm({
      name: `Test Laboratuvar Şablonu ${Math.floor(Math.random() * 900) + 100}`,
      tests: selected,
    });
  };

  const onSave = async () => {
    if (!token || !form.name.trim()) return;
    const itemsPayload = (form.tests || []).map((t) => ({
      labTest: t._id,
      name: t.name,
      unit: t.unit,
      referenceValue: t.referenceValue,
    }));
    try {
      if (editing) {
        await updateDoctorLabTestTemplate({
          token,
          templateId: editing._id,
          name: form.name,
          items: itemsPayload,
        }).unwrap();
        showSuccess("Şablon başarıyla güncellendi");
      } else {
        await createDoctorLabTestTemplate({
          token,
          name: form.name,
          items: itemsPayload,
        }).unwrap();
        showSuccess("Şablon başarıyla oluşturuldu");
      }
      setOpen(false);
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Kaydetme işlemi başarısız.");
    }
  };

  const [togglingStatus, setTogglingStatus] = useState(false);
  const onToggleStatus = async (template) => {
    if (!token) return;
    setTogglingStatus(true);
    try {
      await setDoctorLabTestTemplateStatus({
        token,
        templateId: template._id,
        isActive: !template.isActive,
      }).unwrap();
      showSuccess(template.isActive ? "Şablon pasife alındı" : "Şablon aktifleştirildi");
      setEditing((prev) => (prev && prev._id === template._id ? { ...prev, isActive: !template.isActive } : prev));
      fetchList();
    } catch (e) {
      showError(e?.data?.message || "Durum güncellenemedi.");
    } finally {
      setTogglingStatus(false);
    }
  };

  const canSave = useMemo(() => Boolean(form.name.trim()), [form.name]);

  const subText = { fontSize: TYPO.caption, color: COLORS.slate500 };
  const itemNames = (row) => (row.items || []).map((i) => i?.name).filter(Boolean);

  return (
    <TemplateListPage
      icon={<FaVial size={20} />}
      title="Laboratuvar Şablonları"
      subtitle="Sık istenen tahlil gruplarını tek tıkla eklemek için şablonlar"
      query={query}
      onQueryChange={(v) => {
        setPage(0);
        setQuery(v);
      }}
      status={status}
      onStatusChange={(v) => {
        setPage(0);
        setStatus(v);
      }}
      onCreate={openCreate}
      errorMsg={errorMsg}
      isLoading={isLoading}
      columns={[
        { key: "name", label: "Şablon", width: "34%" },
        { key: "tests", label: "Tahliller", width: "38%" },
        { key: "updated", label: "Güncellendi", width: "14%" },
        { key: "status", label: "Durum", width: "14%" },
      ]}
      rows={items}
      onRowClick={openEdit}
      renderRowCells={(row) => {
        const names = itemNames(row);
        const updated = row.updatedAt || row.createdAt;
        return [
          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800 }} noWrap>
            {row.name}
          </Typography>,
          names.length ? (
            <Box sx={{ minWidth: 0, pr: 2 }}>
              <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} noWrap>
                {names.slice(0, 3).join(", ")}
              </Typography>
              <Typography sx={subText}>
                {names.length} test{names.length > 3 ? ` · +${names.length - 3} daha` : ""}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
          ),
          updated ? (
            <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }} title={fullDateTime(updated)}>
              {relativePast(updated)}
            </Typography>
          ) : (
            <Typography sx={{ ...subText, color: COLORS.slate300 }}>—</Typography>
          ),
          <StatusPill label={row.isActive ? "Aktif" : "Pasif"} tone={row.isActive ? "success" : "neutral"} />,
        ];
      }}
      emptyIcon={<FaVial size={22} />}
      emptyMessage="Sık istediğiniz tahlil gruplarını şablon olarak kaydedin."
      page={page}
      rowsPerPage={rowsPerPage}
      total={total}
      onPageChange={(_, newPage) => setPage(newPage)}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
      }}
    >
      <TemplateEditorDrawer
        open={open}
        onClose={() => setOpen(false)}
        icon={<FaVial size={18} />}
        title={editing ? "Şablonu düzenle" : "Yeni laboratuvar şablonu"}
        subtitle={editing ? editing.name : "Sık kullanılan tahlilleri şablon olarak kaydedin"}
        onSubmit={onSave}
        submitLabel={editing ? "Güncelle" : "Kaydet"}
        submitDisabled={!canSave}
        loading={isSaving}
        statusControl={
          editing
            ? { isActive: Boolean(editing.isActive), onToggle: () => onToggleStatus(editing), loading: togglingStatus }
            : null
        }
        headerExtra={
          !editing ? (
            <Button
              startIcon={<AutoAwesomeIcon />}
              variant="outlined"
              size="small"
              onClick={autoGenerate}
              disabled={labTests.length === 0}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 2,
                borderColor: COLORS.warning,
                color: COLORS.warningDark,
                "&:hover": { bgcolor: COLORS.warningLightest, borderColor: COLORS.warningDark },
              }}
            >
              Otomatik doldur (test)
            </Button>
          ) : null
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <DrawerField label="Şablon adı *">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              placeholder="Ör. Check-up paneli"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              InputProps={tileInputProps(true)}
            />
          </DrawerField>
          <ChipListSelect
            label="Tahliller"
            placeholder="Tahlil ara"
            options={labTests}
            value={form.tests}
            onChange={(value) => setForm((p) => ({ ...p, tests: value || [] }))}
            emptyText="Henüz tahlil eklenmedi."
          />
        </Box>
      </TemplateEditorDrawer>
    </TemplateListPage>
  );
}
